import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { profile, loadProfile } from "../store.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { topbar, backLink } from "../components/common.js";
import { spinner, errorState, openModal, confirmDialog, toast } from "../components/ui.js";
import { fmtVolume, fmtDateTime, relativeDay } from "../lib/format.js";

export default function Admin() {
  const root = h("div", { class: "screen" });
  let reportStatus = "open"; // 'open' | 'all' — toggle for the moderation queue
  load();

  async function load() {
    setChildren(root, backLink("Profile", "/profile"), topbar("Admin"), spinner());
    try {
      if (!profile()) await loadProfile();
      // Defence in depth — the server gates by is_admin anyway, but don't even render
      // the request for a non-admin who reached the route directly.
      if (!profile()?.is_admin) {
        setChildren(root, backLink("Profile", "/profile"), topbar("Admin"),
          errorState("You don't have access to this page.", () => navigate("/")));
        return;
      }
      const [data, reports, invites] = await Promise.all([
        api.adminOverview(), api.adminReports(reportStatus), api.adminInvites(),
      ]);
      render(data, reports, invites);
    } catch (e) {
      setChildren(root, backLink("Profile", "/profile"), topbar("Admin"), errorState(e.message, load));
    }
  }

  function render(d, reports = [], invites = []) {
    const u = d.users, s = d.sessions, soc = d.social, cat = d.catalog;
    const inviteActive = invites.filter((i) => i.is_active).length;
    setChildren(root,
      backLink("Profile", "/profile"),
      topbar("Admin", { sub: "Platform overview" }),

      // ── Headline numbers ──
      h("div", { class: "stat-grid" },
        statCard(String(u.total), "Users", `${u.new_30d} new (30d)`),
        statCard(String(u.active_7d), "Active (7d)", `${u.active_30d} in 30d`),
        statCard(String(s.completed), "Workouts logged", `${s.completed_7d} this week`),
        statCard(
          h("span", null, fmtVolume(s.total_volume_kg).split(" ")[0], h("small", null, " " + fmtVolume(0).split(" ")[1])),
          "Total volume", `${s.total_sets.toLocaleString()} sets`
        )
      ),

      s.active_now > 0
        ? h("div", { class: "card", style: { display: "flex", alignItems: "center", gap: "10px", marginTop: "12px" } },
            h("span", { class: "pulse" }),
            h("div", { style: { fontWeight: 700 } }, `${s.active_now} workout${s.active_now === 1 ? "" : "s"} in progress right now`))
        : null,

      // ── Moderation queue ──
      h("div", { class: "row-between", style: { alignItems: "center", margin: "22px 4px 10px" } },
        h("div", { class: "section-title", style: { margin: 0 } },
          `Reports${d.open_reports ? ` · ${d.open_reports} open` : ""}`),
        h("button", { class: "btn btn-ghost", style: { fontSize: ".78rem", padding: "5px 12px" },
          onClick: () => { reportStatus = reportStatus === "open" ? "all" : "open"; load(); } },
          reportStatus === "open" ? "Show all" : "Show open only")
      ),
      h("div", { class: "card" },
        reports.length
          ? h("div", null, ...reports.map(reportRow))
          : h("p", { class: "dim center" }, reportStatus === "open" ? "No open reports. 🎉" : "No reports yet.")
      ),

      // ── Invite codes ──
      h("div", { class: "section-title" }, `Invite codes${inviteActive ? ` · ${inviteActive} active` : ""}`),
      h("div", { class: "card" },
        inviteGenerator(invites.length > 0),
        invites.length
          ? h("div", null, ...invites.map(inviteRow))
          : h("p", { class: "dim center", style: { margin: "6px 0 0" } }, "No invite codes yet. Generate one to invite someone.")
      ),

      // ── Users ──
      h("div", { class: "section-title" }, "Users"),
      h("div", { class: "card" },
        breakdownRow("Total accounts", u.total),
        breakdownRow("New this week / month", `${u.new_7d} / ${u.new_30d}`),
        breakdownRow("Active (7d / 30d)", `${u.active_7d} / ${u.active_30d}`),
        breakdownRow("Private accounts", u.private),
        breakdownRow("OAuth sign-ins", u.oauth),
        d.invites ? breakdownRow("Invite codes (active / used)", `${d.invites.active} / ${d.invites.used}`) : null,
        breakdownRow("Admins", u.admins)
      ),

      // ── Engagement ──
      h("div", { class: "section-title" }, "Engagement"),
      h("div", { class: "card" },
        breakdownRow("Completed sessions", s.completed),
        breakdownRow("In progress now", s.active_now),
        breakdownRow("Sessions (7d / 30d)", `${s.completed_7d} / ${s.completed_30d}`),
        breakdownRow("Sets logged", s.total_sets.toLocaleString()),
        breakdownRow("Total volume", fmtVolume(s.total_volume_kg)),
        breakdownRow("Bodyweight logs", cat.bodyweight_logs)
      ),

      // ── Content & social ──
      h("div", { class: "section-title" }, "Content & social"),
      h("div", { class: "card" },
        breakdownRow("Workout templates", `${d.workouts.total} (${d.workouts.public} public)`),
        breakdownRow("Programs", `${d.programs.total} (${d.programs.public} public)`),
        breakdownRow("Follows / pending", `${soc.follows} / ${soc.pending_follows}`),
        breakdownRow("Mutual friends", soc.friend_pairs),
        breakdownRow("Kudos given", soc.kudos),
        breakdownRow("Workout ratings", soc.ratings),
        breakdownRow("Push subscriptions", cat.push_subscriptions),
        breakdownRow("Exercise library", `${cat.exercises} exercises · ${cat.muscles} muscles`),
        breakdownRow("User-added exercises", cat.user_exercises)
      ),

      // ── User-created exercises ──
      h("div", { class: "section-title" }, `User-added exercises (${(d.user_exercises || []).length})`),
      h("div", { class: "card" },
        (d.user_exercises || []).length
          ? h("div", null, ...d.user_exercises.map(exerciseRow))
          : h("p", { class: "dim center" }, "No user-added exercises yet.")
      ),

      // ── Members table ──
      h("div", { class: "section-title" }, `Members (${d.roster.length})`),
      h("div", { class: "card", style: { overflowX: "auto", padding: "4px 0" } }, membersTable(d.roster)),

      // ── Recent activity ──
      h("div", { class: "section-title" }, "Recent sessions"),
      h("div", { class: "card" },
        d.recent_sessions.length
          ? h("div", null, ...d.recent_sessions.map(recentRow))
          : h("p", { class: "dim center" }, "No sessions yet.")
      ),

      h("p", { class: "dim center", style: { marginTop: "20px", fontSize: ".72rem" } },
        `${fmtBytes(d.db_size_bytes)} database · generated ${fmtDateTime(d.generated_at)}`)
    );
  }

  function membersTable(roster) {
    const head = h("tr", null,
      th("User"), th("Joined"), th("Last seen"), th("Sessions", "num"), th("Workouts", "num"), th(""));
    const rows = roster.map((m) => h("tr", { class: ["admin-row", m.is_banned && "is-banned"], onClick: () => manageMember(m) },
      h("td", null,
        h("div", { style: { display: "flex", alignItems: "center", gap: "6px" } },
          h("span", { style: { fontWeight: 600 } }, m.display_name || m.username),
          m.is_admin ? h("span", { class: "badge", style: { background: "var(--accent)", color: "#fff" } }, "admin") : null,
          m.is_banned ? h("span", { class: "badge", style: { background: "var(--danger)", color: "#fff" } }, "banned") : null,
          m.is_private ? icon("lock", { size: 12, className: "icon muted" }) : null
        ),
        h("div", { class: "dim", style: { fontSize: ".72rem" } },
          `@${m.username}${m.oauth_provider ? " · " + m.oauth_provider : ""}`)
      ),
      td(relDate(m.created_at)),
      td(m.last_login ? relativeDay(m.last_login) : "never"),
      td(String(m.sessions), "num"),
      td(String(m.workouts), "num"),
      h("td", { class: "ta-right" }, icon("forward", { size: 16, className: "icon muted" }))
    ));
    return h("table", { class: "admin-table" }, h("thead", null, head), h("tbody", null, ...rows));
  }

  // Per-member moderation sheet: ban/deactivate, reactivate, or permanently delete.
  function manageMember(m) {
    const name = m.display_name || m.username;
    const meta = h("div", null,
      h("div", { style: { fontWeight: 700, fontSize: "1.05rem" } }, name),
      h("div", { class: "dim", style: { fontSize: ".82rem" } },
        `@${m.username}${m.oauth_provider ? " · " + m.oauth_provider : ""}`),
      h("div", { class: "dim", style: { fontSize: ".82rem", marginTop: "6px" } },
        `${m.sessions} session${m.sessions === 1 ? "" : "s"} · ${m.workouts} workout${m.workouts === 1 ? "" : "s"} · joined ${relDate(m.created_at)}`)
    );

    let body;
    if (m.is_admin) {
      body = h("div", null, meta,
        h("p", { class: "dim", style: { marginTop: "16px" } }, "Admin accounts are protected — they can't be disabled or deleted here."));
    } else {
      const banBtn = h("button",
        { class: ["btn", "btn-block", "mt", m.is_banned ? "btn-primary" : "btn-ghost"], onClick: () => toggleBan(m) },
        icon(m.is_banned ? "check" : "lock", { size: 18 }),
        m.is_banned ? "Reactivate account" : "Deactivate (ban) account");
      const delBtn = h("button",
        { class: "btn btn-block mt", style: { background: "transparent", color: "var(--danger)" }, onClick: () => deleteMember(m) },
        icon("trash", { size: 18 }), "Delete account permanently");
      body = h("div", null, meta,
        h("p", { class: "dim", style: { marginTop: "14px", fontSize: ".82rem" } },
          m.is_banned
            ? "This account is disabled — it can't log in. Reactivate to restore access."
            : "Disabling keeps the account's data but blocks login. Deleting removes the account and all its workouts, sessions and records."),
        banBtn, delBtn);
    }
    modal = openModal({ title: "Manage member", content: body, className: "modal-confirm" });
  }

  let modal = null;

  async function toggleBan(m) {
    const want = !m.is_banned;
    if (want) {
      const okGo = await confirmDialog({
        title: `Deactivate ${m.display_name || m.username}?`,
        message: "They'll be logged out and unable to sign in until reactivated. Their data is kept.",
        confirmText: "Deactivate", danger: true,
      });
      if (!okGo) return;
    }
    try {
      await api.adminSetBanned(m.id, want);
      toast(want ? "Account deactivated" : "Account reactivated", "success");
      modal?.close();
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  }

  async function deleteMember(m) {
    const okGo = await confirmDialog({
      title: `Delete ${m.display_name || m.username}?`,
      message: `This permanently removes @${m.username} and all their workouts, sessions, and records. This cannot be undone.`,
      confirmText: "Delete everything", danger: true,
    });
    if (!okGo) return;
    try {
      await api.adminDeleteUser(m.id);
      toast("Account deleted", "success");
      modal?.close();
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  }

  function exerciseRow(ex) {
    const inUse = (ex.in_workouts || 0) + (ex.in_sessions || 0);
    const usage = inUse
      ? `in ${ex.in_workouts} workout${ex.in_workouts === 1 ? "" : "s"} · ${ex.in_sessions} session${ex.in_sessions === 1 ? "" : "s"}`
      : "unused";
    return h("div", { class: "row-between", style: { padding: "8px 0", gap: "10px" } },
      h("div", { style: { minWidth: 0 } },
        h("div", { style: { fontWeight: 600 } }, ex.exercise_name),
        h("div", { class: "dim", style: { fontSize: ".78rem" } },
          `by ${ex.creator}${ex.equipment ? " · " + ex.equipment : ""} · ${usage}`)),
      h("button", { class: "icon-btn", style: { color: "var(--danger)", flexShrink: 0 },
        title: inUse ? "In use — can't delete" : "Delete exercise",
        onClick: () => deleteExercise(ex) }, icon("trash", { size: 18 }))
    );
  }

  async function deleteExercise(ex) {
    const okGo = await confirmDialog({
      title: `Delete "${ex.exercise_name}"?`,
      message: `Removes this user-added exercise from the shared library. Created by ${ex.creator}. This cannot be undone.`,
      confirmText: "Delete", danger: true,
    });
    if (!okGo) return;
    try {
      await api.adminDeleteExercise(ex.id);
      toast("Exercise deleted", "success");
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  }

  // Generate-invite control: an optional note + a button. The note labels who it's for.
  function inviteGenerator(hasList) {
    const note = h("input", { class: "input", placeholder: "Note (optional, e.g. \"for Anna\")", maxlength: 100 });
    return h("div", { class: "hstack", style: { gap: "8px", marginBottom: hasList ? "12px" : "0" } },
      note,
      h("button", { class: "btn btn-primary", style: { flexShrink: 0 }, onClick: () => generateInvite(note) },
        icon("plus", { size: 16 }), "Generate"));
  }

  function inviteRow(inv) {
    const used = !!inv.used_by_username;
    const status = used
      ? h("span", { class: "badge" }, `used by @${inv.used_by_username}`)
      : inv.is_active
      ? h("span", { class: "badge", style: { background: "var(--success)", color: "#fff" } }, "active")
      : h("span", { class: "badge" }, "expired");
    return h("div", { class: "row-between", style: { padding: "9px 0", gap: "10px", borderBottom: "1px solid var(--border)" } },
      h("div", { style: { minWidth: 0 } },
        h("div", { class: "hstack", style: { gap: "8px", alignItems: "center", flexWrap: "wrap" } },
          h("code", { class: "invite-code" }, inv.code), status),
        h("div", { class: "dim", style: { fontSize: ".74rem", marginTop: "3px" } },
          `${inv.note ? inv.note + " · " : ""}created ${relDate(inv.created_at)}${inv.expires_at ? " · expires " + relDate(inv.expires_at) : ""}${used && inv.used_at ? " · joined " + relDate(inv.used_at) : ""}`)),
      h("div", { class: "hstack", style: { gap: "2px", flexShrink: 0 } },
        inv.is_active
          ? h("button", { class: "icon-btn", title: "Copy invite link", "aria-label": "Copy invite link", onClick: () => copyInviteLink(inv.code) }, icon("link", { size: 17 }))
          : null,
        !used
          ? h("button", { class: "icon-btn", style: { color: "var(--danger)" }, title: "Revoke", "aria-label": "Revoke invite", onClick: () => revokeInvite(inv) }, icon("trash", { size: 17 }))
          : null)
    );
  }

  async function generateInvite(noteInput) {
    try {
      const note = noteInput?.value?.trim();
      const inv = await api.adminCreateInvite(note ? { note } : {});
      copyInviteLink(inv.code, `Invite ${inv.code} created — link copied`);
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  }

  function copyInviteLink(code, successMsg) {
    const link = `${location.origin}/login#invite=${encodeURIComponent(code)}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link)
        .then(() => toast(successMsg || "Invite link copied", "success"))
        .catch(() => toast(link, "info"));
    } else {
      toast(link, "info");
    }
  }

  async function revokeInvite(inv) {
    const okGo = await confirmDialog({
      title: "Revoke invite?",
      message: `Code ${inv.code} will stop working immediately.`,
      confirmText: "Revoke", danger: true,
    });
    if (!okGo) return;
    try {
      await api.adminDeleteInvite(inv.id);
      toast("Invite revoked", "success");
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  }

  function reportRow(r) {
    const reporter = r.reporter_display_name || r.reporter_username;
    const open = r.status === "open";
    const statusBadge = open
      ? h("span", { class: "badge", style: { background: "var(--danger)", color: "#fff" } }, "open")
      : h("span", { class: "badge" }, r.status);
    return h("div", { style: { padding: "10px 0", borderBottom: "1px solid var(--border)" } },
      h("div", { class: "row-between", style: { alignItems: "center", gap: "8px" } },
        h("div", { style: { minWidth: 0 } },
          h("div", { class: "hstack", style: { gap: "8px", alignItems: "center", flexWrap: "wrap" } },
            h("span", { class: "badge" }, r.target_type),
            statusBadge,
            r.target.path
              ? h("a", { class: "accent", style: { fontWeight: 600, cursor: "pointer" }, onClick: () => navigate(r.target.path) }, r.target.label)
              : h("span", { class: "dim", style: { fontWeight: 600 } }, r.target.label)
          ),
          h("div", { style: { marginTop: "4px", fontSize: ".88rem" } }, r.reason),
          r.details ? h("p", { class: "dim", style: { marginTop: "2px", fontSize: ".82rem" } }, r.details) : null,
          h("div", { class: "dim", style: { fontSize: ".72rem", marginTop: "4px" } },
            `reported by ${reporter} · ${relativeDay(r.created_at)}`
            + (r.status !== "open" ? ` · ${r.status}${r.resolver_username ? " by " + r.resolver_username : ""}` : "")),
          r.resolution_note ? h("div", { class: "dim", style: { fontSize: ".78rem", marginTop: "2px", fontStyle: "italic" } }, `“${r.resolution_note}”`) : null
        )
      ),
      open
        ? h("div", { class: "btn-row mt" },
            h("button", { class: "btn btn-ghost", onClick: () => resolveReport(r, "resolved") }, icon("check", { size: 15 }), "Resolve"),
            h("button", { class: "btn btn-ghost", onClick: () => resolveReport(r, "dismissed") }, icon("x", { size: 15 }), "Dismiss"))
        : h("div", { class: "mt" },
            h("button", { class: "btn btn-ghost", style: { fontSize: ".78rem" }, onClick: () => resolveReport(r, "open") }, "Reopen"))
    );
  }

  async function resolveReport(r, status) {
    try {
      await api.adminResolveReport(r.id, { status });
      toast(status === "open" ? "Report reopened" : `Report ${status}`, "success");
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  }

  function recentRow(r) {
    const who = r.display_name || r.username;
    const active = r.status === "active";
    return h("div", { class: "row-between", style: { padding: "8px 0", cursor: active ? "default" : "pointer" },
        onClick: active ? null : () => navigate(`/history/session/${r.id}`) },
      h("div", null,
        h("div", { style: { fontWeight: 600 } }, r.name || "Workout"),
        h("div", { class: "dim", style: { fontSize: ".8rem" } }, `${who} · ${relativeDay(r.started_at)}`)),
      active
        ? h("span", { class: "hstack", style: { gap: "6px", color: "var(--success)", fontSize: ".8rem" } }, h("span", { class: "pulse" }), "Active")
        : icon("forward", { size: 18, className: "icon muted" })
    );
  }

  function statCard(num, label, sub) {
    return h("div", { class: "stat" },
      h("div", { class: "num accent" }, num),
      h("div", { class: "lbl" }, label),
      sub ? h("div", { class: "dim", style: { fontSize: ".7rem", marginTop: "2px" } }, sub) : null);
  }

  function breakdownRow(label, value) {
    return h("div", { class: "row-between", style: { padding: "7px 0" } },
      h("span", { class: "dim" }, label),
      h("span", { style: { fontWeight: 700 } }, String(value)));
  }

  const th = (label, cls) => h("th", { class: cls === "num" ? "ta-right" : null }, label);
  const td = (val, cls) => h("td", { class: cls === "num" ? "ta-right" : null }, val);

  function relDate(ts) {
    const d = new Date((ts || "").includes("Z") || (ts || "").includes("T") ? ts : (ts || "").replace(" ", "T") + "Z");
    return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
  }

  function fmtBytes(b) {
    if (b == null) return "—";
    if (b < 1024) return `${b} B`;
    if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / 1048576).toFixed(1)} MB`;
  }

  return root;
}
